import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { StarIcon, ShieldCheckIcon, SparklesIcon, CheckCircle2Icon } from 'lucide-react';
const FOOD_IMAGES = ['https://images.unsplash.com/photo-1512621776951-a57141f2eefd?ixlib=rb-4.0.3&fit=crop&fm=webp&w=240&q=70', 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?ixlib=rb-4.0.3&fit=crop&fm=webp&w=240&q=70', 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-4.0.3&fit=crop&fm=webp&w=240&q=70'];
export function Hero() {
  return <section className="relative overflow-hidden bg-brand-900 text-white pt-12 pb-16 lg:pt-20 lg:pb-24" data-id="element-44">
      {/* Subtle texture / depth */}
      <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-45">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-700 blur-3xl" data-id="element-46"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-800 blur-3xl" data-id="element-47"></div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-48">
        {/* Trust pill */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent-400/40 bg-brand-800/60 text-accent-300 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-8" data-id="element-49">
          <SparklesIcon className="w-4 h-4" data-id="element-50" />
          Método Nutricional Validado
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.05
      }} className="font-serif font-bold leading-[1.1] text-4xl sm:text-5xl lg:text-6xl mb-6" data-id="element-51">
          Ayudá a <span className="text-accent-400" data-id="element-52">revertir</span> el{' '}
          <span className="text-[#09E310]" data-id="element-53">Hígado Graso</span> desde tu próxima
          comida
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} className="text-lg sm:text-xl text-brand-50 font-medium leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-54">
          Aprendé qué comer desde hoy y cuidá tu hígado con recetas ricas, menú
          de 7 días y reemplazos simples — sin vivir con miedo, sin dietas
          imposibles y sin dejar de comer rico.
        </motion.p>

        {/* Food strip */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.25
      }} className="grid grid-cols-3 gap-2 sm:gap-3 max-w-xl mx-auto mb-6" data-id="element-55">
          {FOOD_IMAGES.map((src, i) => <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-brand-700 shadow-lg" data-id="element-56">
              <img src={src} alt="Plato saludable de la guía Chau Hígado Graso" width={500} height={375} loading="eager" fetchpriority="high" decoding="sync" className="w-full h-full object-cover" data-id="element-57" />
            </div>)}
        </motion.div>

        {/* Social proof card */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} className="bg-white text-stone-800 rounded-2xl shadow-2xl px-5 py-4 max-w-md mx-auto flex items-center gap-3 mb-8 text-left" data-id="element-58">
          <CheckCircle2Icon className="w-9 h-9 text-brand-600 shrink-0" data-id="element-59" />
          <div data-id="element-60">
            <p className="font-bold text-brand-700 text-sm leading-tight" data-id="element-61">
              Laura Gómez, de General Roca
            </p>
            <p className="text-xs text-stone-500 leading-tight" data-id="element-62">
              Acaba de comprar.
              <br data-id="element-63" />
              Hace 38 minutos · Compra Verificada
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.45
      }} className="flex flex-col items-center" data-id="element-64">
          <Button variant="secondary" size="xl" className="w-full sm:w-auto text-lg shadow-2xl shadow-accent-500/30" data-id="element-65">
            Quiero mis recetas ahora
          </Button>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-brand-100 font-medium mt-6" data-id="element-66">
            <div className="flex items-center gap-1.5" data-id="element-67">
              <div className="flex" data-id="element-68">
                {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-4 h-4 fill-accent-400 text-accent-400" data-id="element-69" />)}
              </div>
              <span className="ml-1" data-id="element-70">+5.000 lectores</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-71">
              <ShieldCheckIcon className="w-4 h-4 text-accent-400" data-id="element-72" />
              <span data-id="element-73">Pago 100% Seguro</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}